import {
  BlockStack,
  Text,
  InlineStack,
  Button,
  SkeletonBodyText,
  Card,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { AttendeeEvent } from "@features/events/types";
import { PlusIcon, DeleteIcon } from "@medentic/polaris-icons";
import { formatStartDate } from "@shared/lib";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import { RenderDate } from "@shared/ui";
import { Currency } from "@shared/ui";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";

interface EventParticipationCardProps {
  events: AttendeeEvent[];
  loading: boolean;
  onAssignToEvent?: () => void;
}

export default function EventParticipationCard({
  events,
  loading,
  onAssignToEvent,
}: EventParticipationCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, attendeeId } = useParams<{
    eventOrganizerId: string;
    attendeeId: string;
  }>();
  const { DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);

  // Delete event mutation
  const { mutate: deleteEventMutation } = useMutation({
    mutationFn: (deleteData: {
      eventId: string;
      ticketId: string;
      paymentId: string;
    }) => {
      return DELETE(
        `/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}/ticket`,
        false,
        deleteData
      );
    },
    onSuccess: () => {
      // Refetch attendee events
      queryClient.invalidateQueries({
        queryKey: ["attendeeEvents", attendeeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["attendeeDetails", attendeeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["attendees"],
      });

      setDeletingEventId(null);
    },
    onError: () => {
      setDeletingEventId(null);
    },
  });

  const handleDeleteEvent = (eventData: AttendeeEvent) => {
    setDeletingEventId(eventData.event.id);
    deleteEventMutation({
      eventId: eventData.event.id,
      ticketId: eventData.ticket.id,
      paymentId: eventData.payment.id,
    });
  };

  if (loading) {
    return <SkeletonBodyText lines={3} />;
  }

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between">
        <Text variant="headingMd" as="h2">
          {t("attendeeDetails.eventParticipation.title")}
        </Text>
        <Button size="slim" icon={PlusIcon} onClick={onAssignToEvent}>
          {t("attendeeDetails.eventParticipation.assignToEvent")}
        </Button>
      </InlineStack>

      <Text variant="bodyMd" as="p" tone="subdued">
        {t("attendeeDetails.eventParticipation.description")}
      </Text>

      {events.length === 0 ? (
        <Card padding="400">
          <Text variant="bodyMd" as="p" tone="subdued">
            {t("attendeeDetails.eventParticipation.noEvents")}
          </Text>
        </Card>
      ) : (
        <Card padding={"0"}>
          <BlockStack gap="0">
            {events.map((eventData, index) => (
              <Box
                key={
                  eventData?.event?.id ??
                  eventData?.payment?.id ??
                  index
                }
              >
                {/* Event Content */}
                <Box padding="400">
                  <InlineStack align="space-between" blockAlign="start">
                    <Box maxWidth="70%">
                      <BlockStack gap="100">
                        <Text
                          variant="bodyMd"
                          as="p"
                          fontWeight="medium"
                          truncate
                        >
                          {eventData?.event?.title || "Unknown Event"}
                        </Text>

                        <InlineStack
                          gap="200"
                          blockAlign="center"
                          align="start"
                        >
                          <Text variant="bodySm" as="p" tone="subdued">
                            {eventData?.event?.dateStart ===
                              eventData?.event?.dateEnd &&
                            eventData?.event?.dateStart ? (
                              <RenderDate
                                dayFirst={true}
                                date={eventData.event.dateStart}
                              />
                            ) : (
                              <>
                                {formatStartDate(eventData?.event?.dateStart)}
                                {eventData?.event?.dateEnd && (
                                  <>
                                    {" "}
                                    -{" "}
                                    <RenderDate
                                      dayFirst={true}
                                      date={eventData.event.dateEnd}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </Text>
                          <Box>
                            <EventBadgeRenderer
                              slug={eventData?.payment?.processedPlatform || ""}
                              entity="payment"
                            />
                          </Box>
                        </InlineStack>
                      </BlockStack>
                    </Box>
                    <InlineStack gap="400" align="end">
                      <Currency
                        value={eventData?.ticket?.price ?? 0}
                        currency={eventData?.ticket?.currency || EVENT_CURRENCY_DEFAULT}
                      />
                      <Button
                        size="micro"
                        icon={DeleteIcon}
                        variant="tertiary"
                        tone="critical"
                        loading={deletingEventId === eventData?.event?.id}
                        onClick={() => handleDeleteEvent(eventData)}
                      />
                    </InlineStack>
                  </InlineStack>
                </Box>
                {/* Separator - only show if not the last item */}
                {index < events.length - 1 && (
                  <Box
                    padding="0"
                    borderBlockEndWidth="025"
                    borderColor="border"
                  />
                )}
              </Box>
            ))}
          </BlockStack>
        </Card>
      )}
    </BlockStack>
  );
}
